/** 
 * Offical Keyboard Documentation
 * https://developer.mozilla.org/en-US/docs/Web/API/UI_Events/Keyboard_event_key_values#whitespace_keys
*/

const toggleKeys = ['Enter', ' '];

export default {
  emits: ['toggle-cell'],
  methods: {
    handleCellNavigation(row = Object, column = Object, event) {
      if (!this.enableCellNavigation || !(event instanceof KeyboardEvent)) {
        return null;
      }
      /** Retrofitting Keyboard Navigation */
      let sibling;
      let cell = event.target;
      if (event.target.nodeName !== 'TD') {
        cell = this.handleCellFetch(row.index, column.index);
      }
      let hasActiveChild = false;
      if (event.key.includes('Arrow')) {
        ({ hasActiveChild } = this.handleCellSanitize(cell, false, true));
      }
      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowRight':
          if (!hasActiveChild) {
            let node;
            do {
              node = (event.key === 'ArrowLeft' ? (node || cell).previousElementSibling : (node || cell).nextElementSibling);
              if (node && node.nodeName === 'TD') {
                /** If cell is hidden || (display:none), move to next one */
                if (node.scrollWidth || node.scrollHeight) sibling = node;
              } else return;
            } while (!sibling && node);
            return (sibling) ? sibling.focus() : null;
          }
          break;
        case 'ArrowUp':
        case 'ArrowDown':
          if (!hasActiveChild) {
            let node;
            const currenTr = cell.parentNode;
            do {
              node = (event.key === 'ArrowUp' ? (node || currenTr).previousElementSibling : (node || currenTr).nextElementSibling);
              if (node && node.nodeName === 'TR') {
                const rowIndex = node.getAttribute('data-row');
                sibling = this.handleCellFetch(rowIndex, column.index);
              } else return;
            } while (!sibling && node);
            return (sibling) ? sibling.focus() : null;
          }
          break;
        case 'Tab':
          if (hasActiveChild) break;
        case ' ':
        case 'Enter':
        case 'Escape':
          this.$emit('toggle-cell', { row, column });
          return (cell) ? this.handleCellEvent(cell, event) : null;
        default:
          break;
      }
    },
    handleCellFetch(rowIndex, colIndex) {
      /** Fetch cell element from references */
      const row = this.$refs[`row_${rowIndex}`];
      const cell = row && row[colIndex];
      return cell;
    },
    handleCellSanitize(cell = {}, checkType = false, sanitize = false) {
      /** Check Cell type && Verify if it has an active child rendered */
      let type, element;
      const hasActiveChild = this.handleCellIsActive(cell);
      if (checkType) {
        const fields = ['c-kebab-menu', 'vcl-checkbox', 'table-field-edit', 'vcl-tooltip'];
        const checkForAnchor = () => {
          const anchors = cell.getElementsByTagName('a');
          if (anchors.length) {
            type = 'hyperlink';
            element = anchors[0];
          }
        }
        for (const field of fields) {
          element = this.handleCellFindChildByClass(cell, field);
          if (!element) continue;
          /** VpFieldEdit Check */
          if (field === 'table-field-edit') {
            type = 'edit';
          }
          /** VpKebabMenu Check */
          else if (field === 'c-kebab-menu') {
            type = 'actions';
          }
          /** VpCheckbox Check */
          else if (field === 'vcl-checkbox') {
            type = 'checkbox';
          }
          /** VpTooltip Check */
          else if (field === 'vcl-tooltip') {
            type = 'tooltip';
          }
          break;
        }
        if (!type) checkForAnchor();
        if (sanitize && hasActiveChild) {
          return { type, hasActiveChild: false, element };
        }
      }
      return { type, hasActiveChild, element };
    },
    handleCellIsActive(cell) {
      let bool = false;
      /** Check if cell has an active child */
      const fields = ['c-kebab-menu', 'table-field-edit', 'vcl-tooltip__body'];
      for (const field of fields) {
        const element = this.handleCellFindChildByClass(cell, field);
        if (!element) continue;
        if (field === 'table-field-edit') {
          const menu = this.handleCellFindChildByClass(cell, 'vcl-tooltip');
          bool = Boolean(menu && this.handleCellFindChildByClass(cell, 'vcl-tooltip__content'));
        }
        if (field === 'c-kebab-menu') {
          const menu = this.handleCellFindChildByClass(cell, 'c-kebab-menu__bubble');
          bool = Boolean(menu && [...menu.classList].includes('is-open'));
        }
        if (field === 'vcl-tooltip__body') {
          const menu = this.handleCellFindChildByClass(cell, 'vcl-tooltip__body');
          bool = Boolean(menu && menu.getAttribute('data-show'));
        }
        break;
      }
      return bool;
    },
    handleCellFindChildByClass(element = HTMLElement, _class = '', enableCollection = false) {
      const target = element && element.children && element.getElementsByClassName(_class);
      return enableCollection ? target : target[0];
    },
    handleCellEvent(cell, event = KeyboardEvent) {
      /** Handle Cell Events */
      if (cell) {
        const { type, hasActiveChild, element } = this.handleCellSanitize(cell, true, false);
        const opts = { element, cell, hasActiveChild, event };
        if (type === 'edit') return this.handleCellFieldEdit(opts);
        if (type === 'actions') return this.handleCellKebabMenu(opts);
        if (type === 'checkbox') return this.handleCellCheckbox(opts);
        if (type === 'tooltip') return this.handleCellTooltip(opts);
        if (type === 'hyperlink') return this.handleCellHyperlink(opts);
      }
      return null;
    },
    /** Child Handlers */
    handleCellFieldEdit({ element, cell, hasActiveChild = false, event = KeyboardEvent }) {
      const registeredKeys = [...toggleKeys, 'Tab', 'Escape'];
      if (!registeredKeys.includes(event.key)) return;

      const toggleFirstInput = () => {
        const tooltips = this.handleCellFindChildByClass(element, 'vcl-tooltip', true);
        let input;
        for (const _el of tooltips) {
          const inputs = _el.getElementsByTagName('input') || [];
          if (inputs.length) {
            input = inputs[0];
            break;
          }
        };
        /** toggle focus on first input within fieldEdit component  */
        return input && input.focus();
      };

      if ([...toggleKeys, 'Tab'].includes(event.key)) {
        if (hasActiveChild || event.key === 'Tab') {
          const isSubmitElement = event.target && event.target.getAttribute('type') === 'submit';
          if (event.key === 'Tab' && isSubmitElement) {
            toggleFirstInput();
            return event.preventDefault();
          }
          return null;
        }
        const editButton = this.handleCellFindChildByClass(element, 'table-field-edit__toggle');
        /** $emit click event on fieldEdit toggle element */
        editButton && editButton.click();
        /** setTimeout to wait for components to render before attempting to find elements */
        return (!editButton) ? null : setTimeout(toggleFirstInput, 100);
      } else if (event.key === 'Escape') {
        const closeBtn = element && element.getElementsByClassName('table-field-edit__close')[0];
        /** $emit click event on fieldEdit close element */
        closeBtn && closeBtn.click();
        return cell.focus();
      }
    },
    handleCellKebabMenu({ element, cell, hasActiveChild = false, event = KeyboardEvent }) {
      const registeredKeys = [...toggleKeys, 'Escape'];
      if (!event || !registeredKeys.includes(event.key)) return;
      const { target } = event;
      const lists = element && element.getElementsByTagName('li') || [];
      const item = [...lists].find(({ classList }) => ![...classList].includes('is-disabled'));
      const isActive = target && [...target.classList].includes('c-kebab-menu__list-item');
      if (toggleKeys.includes(event.key) && !hasActiveChild && !isActive) {
        /** $emit click event on fieldEdit toggle element */
        element && element.focus();
        /** setTimeout to wait for components to render before attempting to find elements */
        return (!element) ? null : setTimeout(() => {
          /** toggle focus on first input within fieldEdit component  */
          return item && item.focus();
        }, 100)
      } else if (event.key === 'Escape' || hasActiveChild) {
        /** $emit blur event on first action element */
        item && item.blur();
        return cell.focus();
      } else if (!toggleKeys.includes(event.key) && isActive) {
        return cell.blur();
      }
      return event.preventDefault();
    },
    handleCellCheckbox({ element, event = KeyboardEvent}) {
      const registeredKeys = [...toggleKeys, 'Escape'];
      if (!registeredKeys.includes(event.key)) return;
      const input = element.getElementsByClassName('vcl-checkbox__input');
      let item;
      if (input) {
        const isArray = input instanceof HTMLCollection || Array.isArray(input);
        item = isArray ? input[0] : input;
      }
      if (event.key === 'Escape') {
        return item && item.checked && item.click();
      }
      return item && item.click();
    },
    handleCellTooltip({ element, cell, hasActiveChild, event = KeyboardEvent }) {
      const registeredKeys = [...toggleKeys, 'Escape'];
      if (!registeredKeys.includes(event.key)) return;
      if (event.target.nodeName === 'A' && event.key !== 'Escape') {
        /** If user is targeting the hyperlink within the tooltip, allow the event to passthrough */
        return this.handleCellHyperlink({ element: event.target, event});
      }
      if (event.key === 'Escape' || hasActiveChild) {
        /** $emit blur event on first action element */
        element && element.blur();
        /** $emit focus event on cell element */
        return cell.focus();
      }

      let item;
      if (element) {
        const isArray = element instanceof HTMLCollection || Array.isArray(element);
        item = isArray ? element[0] : element;
      }
      /** $emit focus event on tooltip target element */
      item && item.focus();
      /** setTimeout to wait for components to render before attempting to find elements */
      return (!item) ? null : setTimeout(() => {
        const body = this.handleCellFindChildByClass(item, 'vcl-tooltip__content');
        /** toggle focus on tooltip body  */
        return body && body.focus();
      }, 100);
    },
    handleCellHyperlink({ element, event = KeyboardEvent }) {
      if (!toggleKeys.includes(event.key) || element.nodeName !== 'A') return;
      return element && element.click();
    }
  }
}
